import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';

import {
	BronzeTrophee,
	FirstBackground,
	GoldTrophee,
	SecondBackground,
	SilverTrophee,
	ThirdBackground,
} from '../../../../assets';
import {
	getMyTournamentStatistics,
	getPlayerTournamentStatistics,
	MyTournamentStatistics,
} from '../../../../services/api';
import Loader from '../../../../shared/Loader';
import { selectIsLogged } from '../../../../store/auth/authReducer';
import { useSafeViewport } from '../../../../utils/hooks/useSafeViewport';
import { IndicatorScrollView } from '../../common/ScrollIndicator';
import { GoldDivider, redesignPanelColors } from '../../Modals/redesignPanelChrome';
import RankCard from './RankCard';
import RankCardMobile from './RankCardMobile';

const emptyBreakdown = {
	'1': 0,
	'2': 0,
	'3': 0,
	total: 0,
};

interface ProfileTournamentsProps {
	playerId?: string;
}

const ProfileTournaments = ({ playerId }: ProfileTournamentsProps) => {
	const { width } = useSafeViewport();
	const isPortrait = width < 1200;
	const { t } = useTranslation();
	const [stats, setStats] = useState<MyTournamentStatistics | null>(null);
	const [loading, setLoading] = useState(true);

	const isLogged = useSelector(selectIsLogged);

	useEffect(() => {
		let mounted = true;

		const loadStats = async () => {
			if (!isLogged) {
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				const result = playerId
					? await getPlayerTournamentStatistics(playerId)
					: await getMyTournamentStatistics();
				if (mounted) setStats(result);
			} catch (e) {
				console.error('Failed to load tournament statistics', e);
				if (mounted) setStats(null);
			} finally {
				if (mounted) setLoading(false);
			}
		};

		loadStats();

		return () => {
			mounted = false;
		};
	}, [isLogged, playerId]);

	const bronze = (stats?.bronze ?? emptyBreakdown) as MyTournamentStatistics['bronze'];
	const silver = (stats?.silver ?? emptyBreakdown) as MyTournamentStatistics['silver'];
	const gold = (stats?.gold ?? emptyBreakdown) as MyTournamentStatistics['gold'];

	const mobileCardWidth = Math.max(260, width * 0.78);
	const desktopModalContentWidth = Math.min(width * 0.9, 800) - 48;
	const desktopCardGap = 10;
	const desktopCardWidth = Math.min(
		235,
		Math.max(180, Math.floor((desktopModalContentWidth - desktopCardGap * 2) / 3)),
	);

	if (loading && !stats) {
		return (
			<View style={styles.loadingWrapper}>
				<Loader />
			</View>
		);
	}

	if (isPortrait) {
		return (
			<View style={styles.mobileWrapper}>
				<Text style={[styles.tournamentTitle, styles.tournamentTitleMobile]}>
					{t('T_SITE_PROFILE_TOURNAMENT_WINNINGS')}
				</Text>

				<GoldDivider />

				<IndicatorScrollView
					contentContainerStyle={[styles.container, styles.mobileScrollContent]}
					showsVerticalScrollIndicator={false}
				>
					<RankCardMobile
						cardWidth={mobileCardWidth}
						backgroundImage={ThirdBackground}
						badge={BronzeTrophee}
						gold={bronze['1']}
						silver={bronze['2']}
						bronze={bronze['3']}
						total={bronze.total}
						color='#FFD700'
					/>
					<RankCardMobile
						cardWidth={mobileCardWidth}
						backgroundImage={SecondBackground}
						badge={SilverTrophee}
						gold={silver['1']}
						silver={silver['2']}
						bronze={silver['3']}
						total={silver.total}
						color='#00F0FF'
					/>
					<RankCardMobile
						cardWidth={mobileCardWidth}
						backgroundImage={FirstBackground}
						badge={GoldTrophee}
						gold={gold['1']}
						silver={gold['2']}
						bronze={gold['3']}
						total={gold.total}
						color='#FFF400'
					/>
				</IndicatorScrollView>
			</View>
		);
	}

	return (
		<View style={styles.desktopWrapper}>
			<Text style={[styles.tournamentTitle, styles.tournamentTitleDesktop]}>
				{t('T_SITE_PROFILE_TOURNAMENT_WINNINGS')}
			</Text>

			<View style={[styles.desktopCardsRow]}>
				<RankCard
					cardWidth={desktopCardWidth}
					backgroundImage={ThirdBackground}
					badge={BronzeTrophee}
					gold={bronze['1']}
					silver={bronze['2']}
					bronze={bronze['3']}
					total={bronze.total}
					color='#FFD700'
				/>
				<RankCard
					cardWidth={desktopCardWidth}
					backgroundImage={SecondBackground}
					badge={SilverTrophee}
					gold={silver['1']}
					silver={silver['2']}
					bronze={silver['3']}
					total={silver.total}
					color='#00F0FF'
				/>
				<RankCard
					cardWidth={desktopCardWidth}
					backgroundImage={FirstBackground}
					badge={GoldTrophee}
					gold={gold['1']}
					silver={gold['2']}
					bronze={gold['3']}
					total={gold.total}
					color='#FFF400'
				/>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		padding: 10,
		alignItems: 'center',
		justifyContent: 'center',
	},
	mobileWrapper: {
		width: '100%',
		alignItems: 'center',
	},
	desktopWrapper: {
		width: '100%',
		alignItems: 'center',
	},
	desktopCardsRow: {
		width: '100%',
		flexDirection: 'row',
		flexWrap: 'nowrap',
		justifyContent: 'center',
		alignItems: 'flex-start',
		gap: 10,
		paddingTop: 80,
		paddingBottom: 34,
	},
	loadingWrapper: {
		width: '100%',
		flexGrow: 1,
		minHeight: 420,
		alignItems: 'center',
		justifyContent: 'center',
	},
	tournamentTitle: {
		fontFamily: 'Sofia Sans',
		fontWeight: '900',
		color: redesignPanelColors.title,
		textAlign: 'center',
		marginVertical: 12,
		textShadowColor: 'rgba(255, 114, 0, 0.6)',
		textShadowOffset: { width: 0, height: 0 },
		textShadowRadius: 6,
		letterSpacing: 1,
		textTransform: 'uppercase',
	},
	tournamentTitleMobile: {
		fontSize: 18,
		marginTop: 0,
	},
	tournamentTitleDesktop: {
		fontSize: 24,
		marginTop: 20,
	},
	mobileScrollContent: {
		flexDirection: 'column',
		gap: 6,
		paddingTop: 8,
		paddingBottom: 0,
		marginBottom: 20,
		paddingHorizontal: 0,
	},
});

export default ProfileTournaments;
